import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { Button, IconButton, Loader, tokens } from '@hotjar/hotsauce';
import type { User } from '@hotjar/user';
import { computedFontStyle, getTextWidth } from '../utils/calculate-text-width';

import { STATUS } from '../hooks/useCommentsApi';
import type { IndexedMentionItem } from '../utils/content-utils';
import { Textarea } from './Textarea';

const INITIAL_CONTENT: [string, Array<IndexedMentionItem>] = ['', []];

const COMMENT_BUTTON_OUTER_WIDTH = 58;

function shouldBreakOnNewLine(text: string, textArea: HTMLTextAreaElement | null) {
  if (textArea === null || !text || !text.length) {
    return false;
  }

  const maxAllowedWidth = textArea.clientWidth - COMMENT_BUTTON_OUTER_WIDTH;
  const textWidth = getTextWidth(text, computedFontStyle(textArea));

  return textWidth > maxAllowedWidth;
}

const Wrapper = styled.section`
  padding-bottom: 0;
  position: relative;
  min-height: 36px;
  min-width: 100%;
  max-width: 100%;
  background-color: white;
`;

const StyledLoader = styled(Loader)`
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  z-index: 1;
`;

const ButtonContainer = styled.div`
  padding: ${tokens.spacer.x1} 0 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Container = styled.div<{
  text: string;
  inlineButton: boolean;
  textArea: HTMLTextAreaElement | null;
}>`
  position: relative;
  border-radius: ${tokens.spacer.x0};
  overflow: hidden;
  min-height: 40px;
  padding-bottom: ${({ text, inlineButton, textArea }) =>
    inlineButton && shouldBreakOnNewLine(text, textArea) ? '30px' : '0'};
  display: flex;
  justify-content: start;
  align-items: center;

  border: 1px solid ${tokens.color.ui_border};
  ${tokens.elevation(1)};

  & .btn-action {
    position: absolute;
    right: 8px;
    bottom: 3px;
    box-shadow: unset !important;
    border-radius: ${tokens.spacer.x0};
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  & .btn-action > button {
    box-shadow: unset !important;
    color: ${tokens.color.text_default};
  }

  & .btn-action:hover {
    background-color: rgba(121, 125, 141, 0.09);
  }

  & > div {
    width: calc(100% - 36px);
    flex-grow: 1;
  }

  & textarea {
    box-shadow: unset !important;
  }

  & textarea:hover {
    box-shadow: unset;
  }
`;

export interface CommentCreatorProps {
  onSave: (content: [string, Array<IndexedMentionItem>]) => void;
  onInteraction: () => void;
  organizationName: string;
  inlineButton: boolean;
  saveButtonText: string;
  users: Array<User>;
  additionalButtons?: ReactNode;
  initialContent?: [string, Array<IndexedMentionItem>];
  inputPlaceholder: string;
  apiStatus: STATUS;
  autofocus: boolean;
  hasOrgInvitePermission: boolean;
}

export const CommentCreator = ({
  organizationName,
  users,
  onSave,
  onInteraction,
  saveButtonText,
  additionalButtons,
  inlineButton,
  initialContent = INITIAL_CONTENT,
  inputPlaceholder,
  apiStatus,
  autofocus,
  hasOrgInvitePermission,
}: CommentCreatorProps): JSX.Element => {
  const [textContent, setContent] = useState<[string, Array<IndexedMentionItem>]>(initialContent);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [currentApiStatus, setCurrentApiStatus] = useState(STATUS.INITIAL);
  const [, setFocus] = useState(false);

  const handleFocus = useCallback(() => {
    setFocus(true);
  }, [setFocus]);

  useEffect(() => {
    if (apiStatus === currentApiStatus) {
      return;
    }

    if (apiStatus === STATUS.SUCCESS && currentApiStatus === STATUS.LOADING) {
      setContent(INITIAL_CONTENT);
    }

    setCurrentApiStatus(apiStatus);
  }, [apiStatus, currentApiStatus, setContent]);

  useEffect(() => {
    if (!autofocus) {
      return () => undefined;
    }

    // NOTE: the timeout is needed, for some unknown reason, for the new thread
    // box - even when both ref and ref.current are defined; for reply or edit
    // box work without it
    const timeout = setTimeout(() => {
      textAreaRef?.current?.focus();
    });

    return () => {
      clearTimeout(timeout);
    };
  }, [autofocus, textAreaRef]);

  return (
    <Wrapper>
      {apiStatus === STATUS.LOADING && <StyledLoader />}
      {apiStatus !== STATUS.LOADING && (
        <>
          <Container
            textArea={textAreaRef.current}
            text={textContent[0]}
            inlineButton={inlineButton}
          >
            <Textarea
              data-testid="comment"
              inputPlaceholder={inputPlaceholder}
              onChange={(text, mentions) => {
                setContent([text, mentions]);
                onInteraction();
              }}
              onFocus={handleFocus}
              organizationName={organizationName}
              ref={textAreaRef}
              users={users}
              value={textContent[0]}
              hasOrgInvitePermission={hasOrgInvitePermission}
            />
            {inlineButton && textContent[0].length > 0 && (
              <IconButton
                className="btn-action"
                icon="send"
                role="button"
                description={saveButtonText}
                isDisabled={!textContent[0].length}
                onClick={() => {
                  onSave(textContent);
                  setFocus(false);
                }}
                tooltipText={saveButtonText}
              />
            )}
          </Container>

          {!inlineButton && (
            <ButtonContainer>
              {additionalButtons}
              <Button
                isDisabled={!textContent[0].length}
                onClick={() => {
                  onSave(textContent);
                  setFocus(false);
                }}
                size="s"
              >
                {saveButtonText}
              </Button>
            </ButtonContainer>
          )}
        </>
      )}
    </Wrapper>
  );
};
